// Pure, immutable operations on a Tree. Every function returns a new Tree object so the
// graph/search caches (keyed by Tree identity) invalidate automatically and undo works by
// keeping old Tree objects around.

import { ancestorsOf, graphOf } from './graph';
import { newId, newPerson } from './people';
import type { Citation, Group, Id, MediaItem, Person, Relationship, Source, Todo, Tree } from './types';

export function newTree(name = 'My Family'): Tree {
  const now = new Date().toISOString();
  return { id: newId('t'), name, people: {}, relationships: {}, groups: {}, sources: {}, media: {}, todos: {}, createdAt: now, updatedAt: now };
}

function touch(t: Tree, patch: Partial<Tree>): Tree {
  return { ...t, ...patch, updatedAt: new Date().toISOString() };
}

/** Dismiss suggestions (by id), or bring them back. */
export function setDismissed(t: Tree, ids: string[], dismissed: boolean): Tree {
  const next = { ...t.dismissed };
  for (const id of ids) {
    if (dismissed) next[id] = true;
    else delete next[id];
  }
  return touch(t, { dismissed: next });
}

export function upsertPerson(t: Tree, p: Person): Tree {
  return touch(t, { people: { ...t.people, [p.id]: p } });
}

export function addNewPerson(t: Tree, partial: Partial<Person> = {}): { tree: Tree; person: Person } {
  const person = newPerson(partial);
  return { tree: upsertPerson(t, person), person };
}

export function removePerson(t: Tree, id: Id): Tree {
  return removePeople(t, [id]);
}

/** Remove several people at once, with their relationships, group memberships, and media and to-do links. */
export function removePeople(t: Tree, ids: Id[]): Tree {
  const gone = new Set(ids.filter((id) => t.people[id]));
  if (!gone.size) return t;
  const people = { ...t.people };
  for (const id of gone) delete people[id];
  const relationships: Record<Id, Relationship> = {};
  for (const [k, r] of Object.entries(t.relationships)) if (!gone.has(r.a) && !gone.has(r.b)) relationships[k] = r;
  const groups: Record<Id, Group> = {};
  for (const [k, g] of Object.entries(t.groups)) {
    groups[k] = g.members.some((m) => gone.has(m.personId)) ? { ...g, members: g.members.filter((m) => !gone.has(m.personId)) } : g;
  }
  const media: Record<Id, MediaItem> = {};
  for (const [k, m] of Object.entries(t.media)) media[k] = m.people.some((x) => gone.has(x.personId)) ? { ...m, people: m.people.filter((x) => !gone.has(x.personId)) } : m;
  const todos: Record<Id, Todo> = {};
  for (const [k, td] of Object.entries(t.todos)) todos[k] = td.people.some((x) => gone.has(x)) ? { ...td, people: td.people.filter((x) => !gone.has(x)) } : td;
  return touch(t, {
    people,
    relationships,
    groups,
    media,
    todos,
    homePersonId: t.homePersonId && gone.has(t.homePersonId) ? undefined : t.homePersonId,
  });
}

/** Media and to-dos with `from` swapped for `to` (or dropped when `to` is null). */
function repointPeople(t: Tree, from: Id, to: Id | null): Pick<Tree, 'media' | 'todos'> {
  const media: Record<Id, MediaItem> = {};
  for (const [k, m] of Object.entries(t.media)) {
    if (!m.people.some((x) => x.personId === from)) {
      media[k] = m;
      continue;
    }
    const people = m.people.flatMap((x) => (x.personId !== from ? [x] : to && !m.people.some((y) => y.personId === to) ? [{ ...x, personId: to }] : []));
    media[k] = { ...m, people };
  }
  const todos: Record<Id, Todo> = {};
  for (const [k, td] of Object.entries(t.todos)) {
    todos[k] = td.people.includes(from) ? { ...td, people: [...new Set(td.people.flatMap((x) => (x !== from ? [x] : to ? [to] : [])))] } : td;
  }
  return { media, todos };
}

export function upsertSource(t: Tree, s: Source): Tree {
  return touch(t, { sources: { ...t.sources, [s.id]: s } });
}

/** Delete a source and every citation of it. Media made from it stay. */
export function removeSource(t: Tree, id: Id): Tree {
  const sources = { ...t.sources };
  delete sources[id];
  const keep = (cs: Citation[] | undefined) => (cs?.some((c) => c.sourceId === id) ? cs.filter((c) => c.sourceId !== id) : cs);
  const people: Record<Id, Person> = {};
  for (const p of Object.values(t.people)) {
    const touched = p.citations?.some((c) => c.sourceId === id) || p.events.some((e) => e.citations?.some((c) => c.sourceId === id));
    people[p.id] = touched ? { ...p, citations: keep(p.citations), events: p.events.map((e) => ({ ...e, citations: keep(e.citations) })) } : p;
  }
  const relationships: Record<Id, Relationship> = {};
  for (const r of Object.values(t.relationships)) relationships[r.id] = r.citations?.some((c) => c.sourceId === id) ? { ...r, citations: keep(r.citations) } : r;
  const media: Record<Id, MediaItem> = {};
  for (const m of Object.values(t.media)) media[m.id] = m.sourceIds?.includes(id) ? { ...m, sourceIds: m.sourceIds.filter((x) => x !== id) } : m;
  return touch(t, { sources, people, relationships, media });
}

export function upsertMedia(t: Tree, m: MediaItem): Tree {
  return touch(t, { media: { ...t.media, [m.id]: m } });
}

export function removeMedia(t: Tree, id: Id): Tree {
  const media = { ...t.media };
  delete media[id];
  return touch(t, { media });
}

export function upsertTodo(t: Tree, td: Todo): Tree {
  return touch(t, { todos: { ...t.todos, [td.id]: td } });
}

export function removeTodo(t: Tree, id: Id): Tree {
  const todos = { ...t.todos };
  delete todos[id];
  return touch(t, { todos });
}

type NewRel = Relationship extends infer R ? (R extends Relationship ? Omit<R, 'id'> & { id?: Id } : never) : never;

export function addRelationship(t: Tree, rel: NewRel): { tree: Tree; rel: Relationship } {
  const full = { ...rel, id: rel.id ?? newId('r') } as Relationship;
  return { tree: upsertRelationship(t, full), rel: full };
}

export function upsertRelationship(t: Tree, r: Relationship): Tree {
  return touch(t, { relationships: { ...t.relationships, [r.id]: r } });
}

export function removeRelationship(t: Tree, id: Id): Tree {
  const relationships = { ...t.relationships };
  delete relationships[id];
  return touch(t, { relationships });
}

export function upsertGroup(t: Tree, g: Group): Tree {
  return touch(t, { groups: { ...t.groups, [g.id]: g } });
}

export function removeGroup(t: Tree, id: Id): Tree {
  const groups = { ...t.groups };
  delete groups[id];
  return touch(t, { groups });
}

/** True if adding "parent → child" would make someone their own ancestor. */
export function wouldCreateCycle(t: Tree, parent: Id, child: Id): boolean {
  if (parent === child) return true;
  // Walk up from parent; if we reach child, child is already an ancestor of parent.
  const seen = new Set<Id>();
  const stack = [parent];
  const parentsOf = new Map<Id, Id[]>();
  for (const r of Object.values(t.relationships)) {
    if (r.kind !== 'parent') continue;
    const arr = parentsOf.get(r.b) ?? [];
    arr.push(r.a);
    parentsOf.set(r.b, arr);
  }
  while (stack.length) {
    const x = stack.pop()!;
    if (x === child) return true;
    if (seen.has(x)) continue;
    seen.add(x);
    stack.push(...(parentsOf.get(x) ?? []));
  }
  return false;
}

/** Find an existing relationship of the same kind between two people (either direction for symmetric kinds). */
export function findRelationship(t: Tree, kind: Relationship['kind'], a: Id, b: Id): Relationship | undefined {
  for (const r of Object.values(t.relationships)) {
    if (r.kind !== kind) continue;
    if (r.a === a && r.b === b) return r;
    if (kind !== 'parent' && r.a === b && r.b === a) return r;
  }
  return undefined;
}

/**
 * Whether merging two people would make someone their own ancestor: true when one is the
 * other's grandparent or further back. (A direct parent link just disappears in the merge.)
 */
export function mergeWouldCreateCycle(t: Tree, a: Id, b: Id): boolean {
  const g = graphOf(t);
  return (ancestorsOf(g, a).get(b) ?? 0) >= 2 || (ancestorsOf(g, b).get(a) ?? 0) >= 2;
}

/**
 * Merge two people who turned out to be the same person. `keep` absorbs `drop`'s names,
 * events, tags, relationships, and group memberships.
 */
export function mergePeople(t: Tree, keep: Id, drop: Id): Tree {
  const k = t.people[keep];
  const d = t.people[drop];
  if (!k || !d || keep === drop) return t;
  const merged = combinePeople(k, d);
  let out: Tree = { ...t, people: { ...t.people, [keep]: merged } };
  const relationships: Record<Id, Relationship> = {};
  for (const r of Object.values(t.relationships)) {
    const nr = { ...r, a: r.a === drop ? keep : r.a, b: r.b === drop ? keep : r.b } as Relationship;
    if (nr.a === nr.b) continue;
    const dup = Object.values(relationships).find(
      (x) => x.kind === nr.kind && ((x.a === nr.a && x.b === nr.b) || (nr.kind !== 'parent' && x.a === nr.b && x.b === nr.a)),
    );
    // Both records had this link: one link, with the details either had (a marriage date, a source).
    if (dup) relationships[dup.id] = combineRels(dup, nr);
    else relationships[nr.id] = nr;
  }
  const groups: Record<Id, Group> = {};
  for (const g of Object.values(t.groups)) {
    const members = g.members.map((m) => (m.personId === drop ? { ...m, personId: keep } : m));
    groups[g.id] = { ...g, members: members.filter((m, i) => members.findIndex((x) => x.personId === m.personId) === i) };
  }
  out = { ...out, relationships, groups, ...repointPeople(t, drop, keep), homePersonId: t.homePersonId === drop ? keep : t.homePersonId };
  return removePerson(out, drop);
}

/** One person built from two records of them: `k` wins where both have a value; lists are unioned. */
function combinePeople(k: Person, d: Person): Person {
  const sameName = (x: Person['names'][number], y: Person['names'][number]) => x.given === y.given && x.surname === y.surname && x.type === y.type;
  // The same name in both keeps whichever details either record has (a nickname, a suffix).
  const names = k.names.map((n) => {
    const twin = d.names.find((m) => sameName(m, n));
    return twin ? { ...n, nickname: n.nickname || twin.nickname, prefix: n.prefix || twin.prefix, suffix: n.suffix || twin.suffix, ...(n.hidden || twin.hidden ? { hidden: true } : {}) } : n;
  });
  return {
    ...k,
    names: [...names, ...d.names.filter((n) => !k.names.some((m) => sameName(m, n)))],
    // A recorded living status or choice of words beats "not recorded".
    living: k.living ?? d.living,
    terms: k.terms ?? d.terms,
    gedcomId: k.gedcomId ?? d.gedcomId,
    events: [...k.events, ...d.events.filter((e) => !k.events.some((x) => x.id === e.id || (x.type === e.type && x.date === e.date && x.place === e.place)))],
    tags: [...new Set([...k.tags, ...d.tags])],
    notes: k.notes === d.notes ? k.notes : [k.notes, d.notes].filter(Boolean).join('\n\n'),
    pronouns: k.pronouns || d.pronouns,
    gender: k.gender.identity === 'unknown' ? d.gender : k.gender,
    photo: k.photo || d.photo,
    citations: unionCitations(k.citations, d.citations),
    passthrough: k.passthrough || d.passthrough ? [...(k.passthrough ?? []), ...(d.passthrough ?? [])] : undefined,
  };
}

/** One link built from two records of it: `k` wins where both have a value; citations are unioned. */
function combineRels(k: Relationship, d: Relationship): Relationship {
  const out = { ...k } as Relationship;
  for (const f of ['start', 'end', 'place', 'label'] as const) if (!out[f] && d[f]) out[f] = d[f];
  if (d.notes && d.notes !== k.notes) out.notes = [k.notes, d.notes].filter(Boolean).join('\n\n');
  // A kind recorded in either beats "unknown".
  if ((out.kind === 'parent' || out.kind === 'sibling') && out.type === 'unknown' && d.kind === out.kind) out.type = d.type as never;
  const citations = unionCitations(k.citations, d.citations);
  if (citations) out.citations = citations;
  if (out.kind === 'partner' && d.kind === 'partner' && d.passthrough?.length) out.passthrough = [...(out.passthrough ?? []), ...d.passthrough];
  return out;
}

function unionCitations(a: Citation[] | undefined, b: Citation[] | undefined): Citation[] | undefined {
  if (!a?.length) return b;
  if (!b?.length) return a;
  return [...a, ...b.filter((c) => !a.some((x) => x.sourceId === c.sourceId && (x.page ?? '') === (c.page ?? '')))];
}

export interface MergeResult {
  tree: Tree;
  /** People new to the tree. */
  added: number;
  /** People who were already here, combined with their record in the file. */
  matched: number;
  relationships: number;
  groups: number;
}

/**
 * Bring another tree into this one. Someone counts as already here when they have the same id
 * (the file came from this tree) or the same name and full birth date as exactly one person
 * here; their details are combined. Everyone else is added. Relationships and groups follow,
 * skipping ones that already exist; a group with the same name and kind is merged.
 */
export function mergeTrees(base: Tree, incoming: Tree): MergeResult {
  const people = { ...base.people };
  const relationships = { ...base.relationships };
  const groups = { ...base.groups };
  const idMap = new Map<Id, Id>();
  // Sources first, so the file's citations point at this tree's sources before anything is
  // combined; otherwise the same citation, through two ids for one source, would be kept twice.
  // The same id (an earlier export of this tree) or the same title and author is one source.
  const sources = { ...base.sources };
  const sourceMap = new Map<Id, Id>();
  const sourceKey = (s: Source) => `${s.title.trim().toLowerCase()}|${(s.author ?? '').trim().toLowerCase()}`;
  for (const s of Object.values(incoming.sources)) {
    const twin = sources[s.id] ? sources[s.id] : Object.values(sources).find((x) => sourceKey(x) === sourceKey(s));
    if (twin) sourceMap.set(s.id, twin.id);
    else sources[s.id] = s;
  }
  const mapCitations = (cs: Citation[] | undefined) => cs?.map((c) => ({ ...c, sourceId: sourceMap.get(c.sourceId) ?? c.sourceId }));
  const withSources = (p: Person): Person =>
    sourceMap.size && (p.citations || p.events.some((e) => e.citations))
      ? { ...p, citations: mapCitations(p.citations), events: p.events.map((e) => (e.citations ? { ...e, citations: mapCitations(e.citations) } : e)) }
      : p;
  const nameKey = (p: Person) => {
    const n = p.names.find((x) => !x.hidden && x.type !== 'deadname');
    const born = p.events.find((e) => e.type === 'birth')?.date;
    return n && born && /\d{1,2} [A-Z]{3} \d{3,4}/.test(born) ? `${n.given.trim().toLowerCase()}|${n.surname.trim().toLowerCase()}|${born}` : null;
  };
  const byKey = new Map<string, Id[]>();
  for (const p of Object.values(base.people)) {
    const k = nameKey(p);
    if (k) byKey.set(k, [...(byKey.get(k) ?? []), p.id]);
  }
  let added = 0;
  let matched = 0;
  for (const p of Object.values(incoming.people).map(withSources)) {
    const key = nameKey(p);
    const same = base.people[p.id] ? p.id : key && byKey.get(key)?.length === 1 ? byKey.get(key)![0] : null;
    if (same) {
      idMap.set(p.id, same);
      people[same] = combinePeople(people[same], { ...p, id: same });
      matched++;
    } else {
      const id = people[p.id] ? newId('p') : p.id;
      idMap.set(p.id, id);
      people[id] = { ...p, id };
      added++;
    }
  }
  // Parents of each person so far, to refuse a parent link that would make someone their own ancestor.
  const parents = new Map<Id, Id[]>();
  for (const r of Object.values(relationships)) if (r.kind === 'parent') parents.set(r.b, [...(parents.get(r.b) ?? []), r.a]);
  const isAncestor = (anc: Id, of: Id) => {
    const stack = [of];
    const seen = new Set<Id>();
    while (stack.length) {
      const x = stack.pop()!;
      if (x === anc) return true;
      if (seen.has(x)) continue;
      seen.add(x);
      stack.push(...(parents.get(x) ?? []));
    }
    return false;
  };
  // Parent links have a direction; the others don't.
  const relKey = (r: Relationship) => `${r.kind}|${r.kind === 'parent' ? `${r.a}>${r.b}` : [r.a, r.b].sort().join('|')}`;
  const seen = new Map(Object.values(relationships).map((r) => [relKey(r), r.id]));
  let relCount = 0;
  for (const r of Object.values(incoming.relationships)) {
    const a = idMap.get(r.a);
    const b = idMap.get(r.b);
    if (!a || !b || a === b) continue;
    const nr = { ...r, a, b, id: relationships[r.id] ? newId('r') : r.id, ...(r.citations ? { citations: mapCitations(r.citations) } : {}) } as Relationship;
    const twin = seen.get(relKey(nr));
    if (twin) {
      // Already here: keep it, adding what the file knows that this tree doesn't (a date, a source).
      relationships[twin] = combineRels(relationships[twin], nr);
      continue;
    }
    if (nr.kind === 'parent') {
      if (isAncestor(nr.b, nr.a)) continue;
      parents.set(nr.b, [...(parents.get(nr.b) ?? []), nr.a]);
    }
    seen.set(relKey(nr), nr.id);
    relationships[nr.id] = nr;
    relCount++;
  }
  let groupCount = 0;
  for (const g of Object.values(incoming.groups)) {
    const members = g.members.filter((m) => idMap.has(m.personId)).map((m) => ({ ...m, personId: idMap.get(m.personId)! }));
    const twin = Object.values(groups).find((x) => x.id === g.id || (x.name.trim().toLowerCase() === g.name.trim().toLowerCase() && x.kind === g.kind));
    if (twin) {
      groups[twin.id] = { ...twin, members: [...twin.members, ...members.filter((m) => !twin.members.some((x) => x.personId === m.personId))] };
    } else {
      const id = groups[g.id] ? newId('g') : g.id;
      groups[id] = { ...g, id, members };
      groupCount++;
    }
  }
  // Media: the same id, or the same file, is one item; its people are combined.
  const media = { ...base.media };
  for (const m of Object.values(incoming.media)) {
    const tagged = m.people.filter((x) => idMap.has(x.personId)).map((x) => ({ ...x, personId: idMap.get(x.personId)! }));
    const sourceIds = m.sourceIds?.map((x) => sourceMap.get(x) ?? x);
    const twin = media[m.id] ?? (m.file ? Object.values(media).find((x) => x.file?.key === m.file!.key) : undefined);
    if (twin) media[twin.id] = { ...twin, people: [...twin.people, ...tagged.filter((x) => !twin.people.some((y) => y.personId === x.personId))] };
    else media[m.id] = { ...m, people: tagged, sourceIds };
  }
  const todos = { ...base.todos };
  for (const td of Object.values(incoming.todos)) {
    const id = todos[td.id] ? (todos[td.id].title === td.title ? null : newId('td')) : td.id;
    if (id) todos[id] = { ...td, id, people: td.people.filter((x) => idMap.has(x)).map((x) => idMap.get(x)!) };
  }
  const passthrough = base.passthrough || incoming.passthrough ? [...(base.passthrough ?? []), ...(incoming.passthrough ?? [])] : undefined;
  // A place this tree has already put on the map keeps its location.
  const locations = base.locations || incoming.locations ? { ...incoming.locations, ...base.locations } : undefined;
  return {
    tree: touch(base, { people, relationships, groups, sources, media, todos, passthrough, locations, homePersonId: base.homePersonId ?? (incoming.homePersonId && idMap.get(incoming.homePersonId)) }),
    added,
    matched,
    relationships: relCount,
    groups: groupCount,
  };
}
